import React, { useState, useEffect } from 'react';
import { FaCopy, FaShare, FaTag, FaClock, FaCalendar, FaEye, FaPalette } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

function ContentCard({ 
  content, 
  onSelect, 
  onCopy, 
  onShare, 
  isSelected, 
  onCheckboxChange 
}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const truncateText = (text, maxLength) => {
    if (!text) return 'Untitled';
    return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCheckboxClick = (e) => {
    e.stopPropagation();
    onCheckboxChange(content.id, e.target.checked);
  };

  const handleCardClick = (e) => {
    // Prevent card click on mobile
    if (!isMobile) {
      onSelect(content);
    }
  };

  const handleViewClick = (e) => {
    e.stopPropagation();
    onSelect(content);
  };

  const handleToneShift = (e) => {
    e.stopPropagation();
    // Navigate to tone shift page with pre-loaded content
    const params = new URLSearchParams({
      content: content.content || '',
      title: content.title || 'Untitled'
    });
            navigate(`/tone-shift?${params.toString()}`);
  };

  return (
    <div 
      className={`content-card ${isSelected ? 'selected' : ''} ${isMobile ? 'mobile' : ''}`}
      onClick={handleCardClick}
    >
      {isMobile ? (
        // Mobile layout
        <>
          <div className="mobile-card-header">
            <div className="mobile-checkbox-container">
              <input
                type="checkbox"
                checked={isSelected}
                onChange={handleCheckboxClick}
                onClick={(e) => e.stopPropagation()}
                className="mobile-checkbox"
              />
            </div>
            <div className="mobile-content-type">
              <FaTag />
              <span>{content.content_type.name}</span>
            </div>
          </div>
          
          <h3 className="mobile-card-title">{truncateText(content.title, 144)}</h3>
          
          <div className="mobile-content-description">
            {truncateText(content.description || 'No description', 480)}
          </div>
          
          <div className="mobile-content-dates">
            <div className="mobile-date-item">
              <FaCalendar /> Created: {formatDateTime(content.created_at)}
            </div>
            <div className="mobile-date-item">
              <FaClock /> Updated: {formatDateTime(content.updated_at)}
            </div>
            {content.shared_links?.length > 0 && content.shared_links[0].view_count !== undefined && (
              <div className="mobile-view-count">
                <FaEye /> {content.shared_links[0].view_count} views
              </div>
            )}
          </div>
          
          <div className="mobile-content-actions">
            <button
              className="icon-button"
              onClick={(e) => onCopy(e, content)}
              title="Copy prompt"
            >
              <FaCopy />
              <span className="action-label">Copy</span>
            </button>
            <div className="share-button-container">
              <button
                className="icon-button"
                onClick={(e) => onShare(e, content)}
                title="Share prompt"
              >
                <FaShare />
                <span className="action-label">Share</span>
                {content.shared_links?.length > 0 && (
                  <span className="share-count-mobile">{content.shared_links.length}</span>
                )}
              </button>
            </div>
            <button
              className="icon-button"
              onClick={handleToneShift}
              title="Tone Shift"
            >
              <FaPalette />
              <span className="action-label">Tone Shift</span>
            </button>
            <button
              className="icon-button view-button"
              onClick={handleViewClick}
              title="View prompt"
            >
              <FaEye />
              <span className="action-label">View</span>
            </button>
          </div>
        </>
      ) : (
        // Desktop layout
        <>
          <div className="content-card-header">
            <div className="content-top">
              <div className="content-checkbox">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={handleCheckboxClick}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
              <div className="content-type">
                <FaTag />
                {content.content_type.name}
              </div>
            </div>

            <h3>{truncateText(content.title, 144)}</h3>

            <div className="content-actions">
              <button
                className="icon-button"
                onClick={(e) => onCopy(e, content)}
                title="Copy prompt"
              >
                <FaCopy />
              </button>
              <div className="share-button-container">
                <button
                  className="icon-button"
                  onClick={(e) => onShare(e, content)}
                  title="Share prompt"
                >
                  <FaShare />
                </button>
                {content.shared_links?.length > 0 && (
                  <div className="share-badge" title="This prompt has been shared">
                    <span className="share-count">{content.shared_links.length}</span>
                  </div>
                )}
              </div>
              <button
                className="icon-button"
                onClick={handleToneShift}
                title="Tone Shift"
              >
                <FaPalette />
              </button>
            </div>
          </div>

          <div className="content-description">
            {truncateText(content.description || 'No description', 480)}
          </div>

          <div className="content-dates">
            <div className="date-items-container">
              <div className="date-item">
                <FaCalendar /> Created: {formatDateTime(content.created_at)}
              </div>
              <div className="date-item">
                <FaClock /> Updated: {formatDateTime(content.updated_at)}
              </div>
            </div>
            {content.shared_links?.length > 0 && content.shared_links[0].view_count !== undefined && (
              <div className="view-count">
                <FaEye /> {content.shared_links[0].view_count} views
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

ContentCard.propTypes = {
  content: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    content: PropTypes.string.isRequired,
    content_type: PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    }).isRequired,
    shared_links: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      view_count: PropTypes.number
    })),
    created_at: PropTypes.string.isRequired,
    updated_at: PropTypes.string.isRequired
  }).isRequired,
  onSelect: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  onCheckboxChange: PropTypes.func.isRequired
};

export default ContentCard; 